/**
 * CalibrationMoreDrawer - the right-hand "More details" panel of the
 * calibration page: batch and sampling record, which sensor cylinders count,
 * the ASTM C1074 checklist and the version history. Keeps the main form to
 * the fields people use every time.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import calibrationsService, { Calibration } from '../../services/calibrations';
import { apiErrorText } from '../../utils/apiError';
import CalibrationChecklist from './CalibrationChecklist';
import { apiLocalToInput, isSystemType } from './calibrationFormat';

const TEXT_FIELDS: [keyof Calibration, string][] = [
  ['docket_number', 'Docket number'],
  ['load_size', 'Load size'],
  ['target_workability', 'Target slump / workability'],
  ['actual_workability', 'Actual slump / workability'],
  ['air_content', 'Air content (%)'],
  ['fresh_density', 'Fresh density'],
  ['concrete_temp', 'Concrete temperature'],
  ['ambient_temp', 'Ambient temperature'],
  ['sampled_location', 'Sampled at'],
  ['sampling_method', 'Sampling method'],
  ['specimen_cast_no', 'Number of cylinders cast'],
  ['notification_contact', 'Notification contact'],
];

interface Props {
  open: boolean;
  onClose: () => void;
  calibration: Calibration;
  readOnly: boolean;
  onSaved: () => void;
  onError: (message: string) => void;
}

const CalibrationMoreDrawer: React.FC<Props> = ({ open, onClose, calibration, readOnly, onSaved, onError }) => {
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const next: Record<string, string> = {};
    TEXT_FIELDS.forEach(([k]) => { next[k] = (calibration[k] as string) || ''; });
    next.comment = calibration.comment || '';
    next.cast_time = apiLocalToInput(calibration.cast_time);
    setForm(next);
  }, [calibration]);

  const dirty = TEXT_FIELDS.some(([k]) => (form[k] ?? '') !== ((calibration[k] as string) || ''))
    || (form.comment ?? '') !== (calibration.comment || '')
    || (form.cast_time ?? '') !== apiLocalToInput(calibration.cast_time);

  const save = async () => {
    setSaving(true);
    try {
      const body: Record<string, string> = {};
      TEXT_FIELDS.forEach(([k]) => { body[k] = (form[k] || '').trim(); });
      body.comment = (form.comment || '').trim();
      if (form.cast_time) body.cast_time = form.cast_time;
      await calibrationsService.update(calibration.id, body as Partial<Calibration>);
      onSaved();
    } catch (e) {
      onError(apiErrorText(e, 'The details could not be saved'));
    } finally {
      setSaving(false);
    }
  };

  const setAccept = async (slot: 1 | 2, value: boolean) => {
    try {
      await calibrationsService.update(calibration.id, { [`speciment${slot}_accept`]: value } as Partial<Calibration>);
      onSaved();
    } catch (e) {
      onError(apiErrorText(e, 'The sensor could not be updated'));
    }
  };

  const specimens = (calibration.specimens || []).filter((s) => s.slot <= 2);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 440 } } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>More details</Typography>
        <IconButton onClick={onClose} aria-label="close"><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
        {calibration.checks && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>ASTM C1074 checklist</Typography>
            <CalibrationChecklist checks={calibration.checks.checks} dense />
          </Box>
        )}

        {isSystemType(calibration.calibration_type) && specimens.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Sensor cylinders that count</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Turn a sensor off if its cylinder was damaged or the sensor failed. Every result is averaged again.
            </Typography>
            {specimens.map((s) => (
              <FormControlLabel
                key={s.slot}
                control={
                  <Switch
                    size="small"
                    checked={s.accepted}
                    disabled={readOnly}
                    onChange={(e) => setAccept(s.slot as 1 | 2, e.target.checked)}
                  />
                }
                label={`Specimen ${s.slot}: ${s.logger_code || ''} channel ${s.channel_no ?? ''}, ${s.name}`}
              />
            ))}
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" gutterBottom>Batch and sampling</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            {TEXT_FIELDS.map(([k, label]) => (
              <TextField
                key={k}
                label={label}
                size="small"
                value={form[k] ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                disabled={readOnly}
                inputProps={{ maxLength: 50 }}
              />
            ))}
            <TextField
              label="Cylinders cast at"
              type="datetime-local"
              size="small"
              value={form.cast_time ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, cast_time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              disabled={readOnly}
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label="Comment"
              size="small"
              multiline
              minRows={2}
              value={form.comment ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
              disabled={readOnly}
              inputProps={{ maxLength: 500 }}
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
          {!readOnly && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={save}
                disabled={saving || !dirty}
              >
                Save details
              </Button>
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>History</Typography>
          <Typography variant="body2">Version {calibration.version_no ?? 1}
            {calibration.parent_code ? `, from ${calibration.parent_code}` : ''}</Typography>
          <Typography variant="body2" color="text.secondary">
            Created {new Date(calibration.create_date).toLocaleString()}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CalibrationMoreDrawer;
